import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import type { SpeciesApi } from "@/lib/api";
import { SPECIES_ORIGIN_LABELS, SPECIES_SIZE_LABELS, codeLabel } from "@/config/projetos-config";

interface SpeciesTableProps {
  species: SpeciesApi[];
}

const PROFILE_BADGE: Record<string, { label: string; tone: string; icon: string }> = {
  REVISADO: { label: "Revisada", tone: "bg-primary-50 text-primary-700", icon: "ri-shield-check-line" },
  EM_REVISAO: { label: "Em revisão", tone: "bg-accent-50 text-accent-700", icon: "ri-time-line" },
  RASCUNHO_IA: { label: "Rascunho IA", tone: "bg-warning-50 text-warning-700", icon: "ri-robot-2-line" },
  SEM_FICHA: { label: "Sem ficha", tone: "bg-background-100 text-foreground-500", icon: "ri-file-add-line" },
};

/** Catalogo de especies real (cta.tree_species). Campo vazio aparece como "—". */
export default function SpeciesTable({ species }: SpeciesTableProps) {
  const [search, setSearch] = useState("");
  const [filterFamily, setFilterFamily] = useState("");
  const [filterOrigin, setFilterOrigin] = useState("");
  const [filterProfile, setFilterProfile] = useState("");

  const families = useMemo(
    () => Array.from(new Set(species.map((s) => s.family_name).filter((f): f is string => !!f))).sort(),
    [species]
  );
  const origins = useMemo(
    () => Array.from(new Set(species.map((s) => s.origin).filter((o): o is string => !!o))).sort(),
    [species]
  );

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return species.filter((s) => {
      if (
        q &&
        !s.popular_name.toLowerCase().includes(q) &&
        !s.scientific_name.toLowerCase().includes(q) &&
        !(s.family_name ?? "").toLowerCase().includes(q)
      )
        return false;
      if (filterFamily && s.family_name !== filterFamily) return false;
      if (filterOrigin && s.origin !== filterOrigin) return false;
      if (filterProfile && (s.profile_review_status ?? "SEM_FICHA") !== filterProfile) return false;
      return true;
    });
  }, [species, search, filterFamily, filterOrigin, filterProfile]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm" />
          <input
            type="text"
            placeholder="Buscar por nome popular, científico ou família..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2 text-sm border border-background-200 rounded-lg bg-white text-foreground-900 focus:ring-2 focus:ring-primary-200 focus:border-primary-500 outline-none"
          />
        </div>
        <select value={filterFamily} onChange={(e) => setFilterFamily(e.target.value)} className="px-3 py-2 text-sm border border-background-200 rounded-lg bg-white text-foreground-900 focus:ring-2 focus:ring-primary-200 focus:border-primary-500 outline-none">
          <option value="">Todas as famílias</option>
          {families.map((f) => <option key={f} value={f}>{f}</option>)}
        </select>
        <select value={filterOrigin} onChange={(e) => setFilterOrigin(e.target.value)} className="px-3 py-2 text-sm border border-background-200 rounded-lg bg-white text-foreground-900 focus:ring-2 focus:ring-primary-200 focus:border-primary-500 outline-none">
          <option value="">Todas as origens</option>
          {origins.map((o) => <option key={o} value={o}>{codeLabel(SPECIES_ORIGIN_LABELS, o)}</option>)}
        </select>
        <select value={filterProfile} onChange={(e) => setFilterProfile(e.target.value)} aria-label="Situação da ficha botânica" className="px-3 py-2 text-sm border border-background-200 rounded-lg bg-white text-foreground-900 focus:ring-2 focus:ring-primary-200 focus:border-primary-500 outline-none">
          <option value="">Todas as fichas</option>
          {Object.entries(PROFILE_BADGE).map(([code, b]) => <option key={code} value={code}>{b.label}</option>)}
        </select>
      </div>

      <div className="text-xs text-foreground-500">{filtered.length} de {species.length} espécies</div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-background-200">
              <th className="text-left py-3 px-3 text-xs font-medium text-foreground-500">Nome popular</th>
              <th className="text-left py-3 px-3 text-xs font-medium text-foreground-500">Nome científico</th>
              <th className="text-left py-3 px-3 text-xs font-medium text-foreground-500">Família</th>
              <th className="text-left py-3 px-3 text-xs font-medium text-foreground-500">Origem</th>
              <th className="text-left py-3 px-3 text-xs font-medium text-foreground-500">Porte</th>
              <th className="text-left py-3 px-3 text-xs font-medium text-foreground-500">Exemplares</th>
              <th className="text-left py-3 px-3 text-xs font-medium text-foreground-500">Status</th>
              <th className="text-left py-3 px-3 text-xs font-medium text-foreground-500">Ficha botânica</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((s) => (
              <tr key={s.id} className="border-b border-background-100 hover:bg-background-50/50 transition-colors">
                <td className="py-2.5 px-3 text-foreground-900 font-medium">
                  <Link to={`/especies/${s.id}`} className="hover:text-primary-700 hover:underline">{s.popular_name}</Link>
                  {s.protected_species && <span className="ml-2 badge bg-primary-50 text-primary-700 text-[10px]">Protegida</span>}
                  {s.invasive_species && <span className="ml-2 badge bg-danger-50 text-danger-700 text-[10px]">Invasora</span>}
                </td>
                <td className="py-2.5 px-3 text-foreground-500 italic">{s.scientific_name}</td>
                <td className="py-2.5 px-3 text-foreground-700">{s.family_name ?? "—"}</td>
                <td className="py-2.5 px-3 text-xs text-foreground-600">{codeLabel(SPECIES_ORIGIN_LABELS, s.origin)}</td>
                <td className="py-2.5 px-3 text-xs text-foreground-600">{codeLabel(SPECIES_SIZE_LABELS, s.tree_size)}</td>
                <td className="py-2.5 px-3 tabular-nums text-foreground-700">{s.registered_tree_count}</td>
                <td className="py-2.5 px-3 text-xs text-foreground-600">{codeLabel({}, s.status)}</td>
                <td className="py-2.5 px-3">
                  {(() => {
                    const b = PROFILE_BADGE[s.profile_review_status ?? "SEM_FICHA"];
                    return (
                      <Link to={`/especies/${s.id}`} className={`badge ${b.tone} hover:underline`}>
                        <i className={`${b.icon} mr-1`} />{b.label}
                      </Link>
                    );
                  })()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {filtered.length === 0 && (
        <div className="text-center py-10 text-foreground-400">
          <i className="ri-plant-line text-3xl mb-2 block" />
          <p className="text-sm">{species.length === 0 ? "Nenhuma espécie cadastrada." : "Nenhuma espécie encontrada com os filtros atuais."}</p>
        </div>
      )}
    </div>
  );
}
